import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import { useI18n } from "../i18n";
import {
  ApiError,
  createReservation,
  getConfig,
  getSlots,
  type ReserveResult,
  type SlotPublic,
} from "../lib/api";
import { themeStyle } from "../lib/theme";
import { localizedDate } from "../../shared/dates";
import { EVENT_DAYS, ROUND_TIMES, themeForDate } from "../../shared/event";
import { Turnstile } from "../components/Turnstile";
import {
  ArrowLeft, Clock, Minus, Plus, CheckCircle2, Download, Ticket,
} from "../components/Icon";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseSlotId(id: string): { date: string; round: number } | null {
  const m = id.match(/^(\d{4}-\d{2}-\d{2})_R([123])$/);
  return m ? { date: m[1], round: Number(m[2]) } : null;
}

export function Reserve() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();

  const [config, setConfig] = useState<{ turnstileSitekey: string; maxPartySize: number }>({
    turnstileSitekey: "",
    maxPartySize: 8,
  });
  const [slots, setSlots] = useState<SlotPublic[]>([]);
  const [date, setDate] = useState<string | null>(null);
  const [round, setRound] = useState<number | null>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [size, setSize] = useState(1);
  const [token, setToken] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReserveResult | null>(null);

  const refreshSlots = () => getSlots().then(setSlots).catch(() => {});

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
    refreshSlots();
    const pre = params.get("slot");
    if (pre) {
      const p = parseSlotId(pre);
      if (p) {
        setDate(p.date);
        setRound(p.round);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const slotMap = useMemo(() => new Map(slots.map((s) => [s.id, s])), [slots]);
  const theme = date ? themeForDate(date) : "brand";
  const slotId = date && round ? `${date}_R${round}` : null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = t("reserve.errors.name");
    if (phone.trim().length < 6) e.phone = t("reserve.errors.phone");
    if (!EMAIL_RE.test(email.trim())) e.email = t("reserve.errors.email");
    if (!slotId) e.slot = t("reserve.errors.slot");
    if (config.turnstileSitekey && !token) e.turnstile = t("reserve.errors.turnstile");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setSubmitError("");
    if (!validate() || !slotId) return;
    setSubmitting(true);
    try {
      const res = await createReservation({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        party_size: size,
        slot_id: slotId,
        lang,
        turnstileToken: token || undefined,
      });
      setResult(res);
      window.scrollTo({ top: 0 });
    } catch (err) {
      if (err instanceof ApiError && err.code === "slot_full") setSubmitError(t("reserve.errors.full"));
      else if (err instanceof ApiError && err.code === "turnstile_failed")
        setSubmitError(t("reserve.errors.turnstile"));
      else setSubmitError(t("reserve.errors.generic"));
      refreshSlots();
    } finally {
      setSubmitting(false);
    }
  }

  function resetAll() {
    setResult(null);
    setName("");
    setPhone("");
    setEmail("");
    setSize(1);
    setToken("");
    setErrors({});
    setSubmitError("");
    setRound(null);
    refreshSlots();
  }

  if (result) return <Success result={result} onReset={resetAll} />;

  return (
    <div style={themeStyle(theme)} className="container-x max-w-2xl py-10">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-[var(--theme-deep)]">
        <ArrowLeft className="h-4 w-4" /> {t("common.backHome")}
      </Link>
      <h1 className="section-title mt-3">{t("reserve.title")}</h1>
      <p className="mt-1 text-ink/70">{t("reserve.subtitle")}</p>

      <form onSubmit={onSubmit} className="mt-7 space-y-6">
        {/* Day + round selection — every round shows its live remaining seats */}
        <div>
          <label className="field-label">{t("reserve.round")}</label>
          <div className="space-y-4">
            {EVENT_DAYS.map((d, i) => (
              <div key={d.date} style={themeStyle(d.theme)}>
                <div className="mb-2 flex items-center gap-2 text-sm">
                  <span className="rounded-full bg-[var(--theme-soft)] px-2.5 py-0.5 font-bold text-[var(--theme-deep)]">
                    Day {i + 1}
                  </span>
                  <span className="font-semibold text-ink/70">{localizedDate(d.date, lang)}</span>
                </div>
                <div className="grid gap-2.5 sm:grid-cols-3">
                  {ROUND_TIMES.map((r) => {
                    const id = `${d.date}_R${r.round}`;
                    const s = slotMap.get(id);
                    const left = s?.remaining;
                    const full = left !== undefined && left <= 0;
                    const selected = date === d.date && round === r.round;
                    return (
                      <button
                        type="button"
                        key={id}
                        disabled={full}
                        onClick={() => {
                          setDate(d.date);
                          setRound(r.round);
                        }}
                        className={`rounded-2xl px-3 py-2.5 text-center ring-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          selected
                            ? "bg-[var(--theme-main)] text-white ring-transparent"
                            : "bg-white text-ink ring-black/10 hover:ring-[var(--theme-main)]"
                        }`}
                      >
                        <div className="inline-flex items-center gap-1.5 text-sm font-bold">
                          <Clock className="h-4 w-4" /> {r.start}–{r.end}
                        </div>
                        <div
                          className={`mt-0.5 text-xs ${
                            selected ? "opacity-90" : full ? "text-red-500" : "text-[var(--theme-deep)]"
                          }`}
                        >
                          {left === undefined
                            ? `${t("schedule.checkIn")} ${r.checkInFrom}`
                            : full
                              ? t("common.full")
                              : t("common.seatsLeft", { n: left })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {errors.slot && <p className="mt-1 text-sm text-red-500">{errors.slot}</p>}
        </div>

        {/* Party size */}
        <div>
          <label className="field-label">{t("reserve.partySize")}</label>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setSize((s) => Math.max(1, s - 1))} className="btn-ghost h-12 w-12 !px-0">
              <Minus className="h-5 w-5" />
            </button>
            <div className="w-14 text-center text-3xl font-bold text-[var(--theme-deep)]">{size}</div>
            <button
              type="button"
              onClick={() => setSize((s) => Math.min(config.maxPartySize, s + 1))}
              className="btn-ghost h-12 w-12 !px-0"
            >
              <Plus className="h-5 w-5" />
            </button>
            <span className="text-sm text-ink/55">{t("reserve.maxPartyNote", { n: config.maxPartySize })}</span>
          </div>
        </div>

        {/* Contact */}
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label={t("reserve.name")} error={errors.name}>
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </Field>
          <Field label={t("reserve.phone")} error={errors.phone}>
            <input className="field" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
          </Field>
        </div>
        <Field label={t("reserve.email")} error={errors.email} hint={t("reserve.emailHint")}>
          <input className="field" value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="email" />
        </Field>

        {config.turnstileSitekey && (
          <div>
            <Turnstile sitekey={config.turnstileSitekey} onToken={setToken} />
            {errors.turnstile && <p className="mt-1 text-center text-sm text-red-500">{errors.turnstile}</p>}
          </div>
        )}

        {submitError && (
          <div className="rounded-2xl bg-red-50 px-4 py-3 text-center text-sm font-semibold text-red-600">
            {submitError}
          </div>
        )}

        <button type="submit" disabled={submitting} className="btn-primary w-full py-4 text-lg">
          <Ticket className="h-5 w-5" /> {submitting ? t("reserve.submitting") : t("reserve.submit")}
        </button>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-ink/50">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}

function Success({ result, onReset }: { result: ReserveResult; onReset: () => void }) {
  const { t, lang } = useI18n();
  const slot = result.reservation.slot;
  const manageUrl = `${window.location.origin}/manage/${result.manage_token}`;
  const theme = themeForDate(slot.event_date);
  const [qr, setQr] = useState("");

  useEffect(() => {
    QRCode.toDataURL(manageUrl, { width: 320, margin: 1 }).then(setQr).catch(() => {});
  }, [manageUrl]);

  return (
    <div style={themeStyle(theme)} className="container-x max-w-xl py-12 text-center">
      <span className="icon-badge mx-auto h-16 w-16"><CheckCircle2 className="h-9 w-9" /></span>
      <h1 className="section-title mt-4">{t("reserve.successTitle")}</h1>
      <p className="mt-2 text-ink/70">{t("reserve.successDesc")}</p>

      <div className="card mt-6 text-left">
        <div className="text-center">
          <div className="text-sm text-ink/50">{t("reserve.yourCode")}</div>
          <div className="text-3xl font-bold tracking-widest text-[var(--theme-deep)]">{result.code}</div>
        </div>

        {/* QR of the manage link */}
        {qr && (
          <div className="mt-5 flex flex-col items-center">
            <img src={qr} alt="reservation QR" className="h-44 w-44 rounded-2xl bg-white p-2 ring-1 ring-black/[0.06]" />
            <p className="mt-2 max-w-xs text-center text-xs text-ink/55">{t("reserve.qrHint")}</p>
            <a href={qr} download={`reservation-${result.code}.png`} className="btn-ghost mt-3 px-4 py-2 text-sm">
              <Download className="h-4 w-4" /> {t("reserve.saveImage")}
            </a>
          </div>
        )}

        <dl className="mt-5 space-y-2 border-t border-black/[0.06] pt-4 text-[15px]">
          <Row k={t("reserve.name")} v={result.reservation.name} />
          <Row k={t("reserve.day")} v={localizedDate(slot.event_date, lang)} />
          <Row k={t("reserve.round")} v={`${slot.start_time}–${slot.end_time}`} />
          <Row k={t("reserve.partySize")} v={`${result.reservation.party_size}`} />
        </dl>
      </div>

      {result.email_status === "sent" && <p className="mt-3 text-sm text-ink/60">{t("reserve.emailSent")}</p>}
      {result.email_status === "skipped" && <p className="mt-3 text-sm text-ink/60">{t("reserve.emailSkipped")}</p>}
      {result.email_status === "failed" && <p className="mt-3 text-sm text-amber-600">{t("reserve.emailFailed")}</p>}
      <p className="mt-1 text-sm font-semibold text-[var(--theme-deep)]">{t("reserve.screenshotTip")}</p>

      <div className="mt-6 rounded-2xl bg-white px-4 py-3 text-left text-sm ring-1 ring-black/[0.05]">
        <div className="text-xs text-ink/50">{t("reserve.manageLink")}</div>
        <a href={manageUrl} className="break-all font-medium text-[var(--theme-deep)]">{manageUrl}</a>
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <button onClick={onReset} className="btn-ghost">{t("reserve.reserveAnother")}</button>
        <Link to="/" className="btn-primary">{t("common.backHome")}</Link>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink/50">{k}</dt>
      <dd className="font-semibold text-ink">{v}</dd>
    </div>
  );
}
